import { useState } from 'react';
import { api, useAction } from '../../../api/hooks';
import { Button } from '../../../ui/Button';
import { Alert, ErrorAlert } from '../../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../../ui/forms';
import { Modal } from '../../../ui/Modal';
import type { AttemptDetail } from '../types';
import { ChecksEditor } from './ChecksEditor';

export function ReconcileDialog({
  a,
  base,
  onClose,
  inv,
}: {
  a: AttemptDetail;
  base: string;
  onClose: () => void;
  inv: unknown[][];
}) {
  const [outcome, setOutcome] = useState<'success' | 'failure'>('failure');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [checks, setChecks] = useState<Record<string, { passed: boolean; observation: string }>>({});
  const m = useAction(
    () =>
      api.post(`${base}/reconcile`, {
        outcome,
        reason,
        evidence,
        checks: Object.entries(checks).map(([name, r]) => ({ name, ...r })),
      }),
    { success: 'Attempt reconciled', invalidate: inv as never, onSuccess: onClose },
  );
  return (
    <Modal
      title={`Reconcile ${a.attempt}`}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => m.mutate(undefined)}
            loading={m.isPending}
            disabled={!reason.trim() || !evidence.trim()}
          >
            Record reconciliation
          </Button>
        </>
      }
    >
      <Alert tone="info">
        Record what you verified in the real environment. Success requires passing results for every required
        check and closes eligible tickets with verified provenance.
      </Alert>
      <Field label="Observed outcome">
        {(p) => (
          <Select {...p} value={outcome} onChange={(e) => setOutcome(e.target.value as never)}>
            <option value="failure">Failure</option>
            <option value="success">Success</option>
          </Select>
        )}
      </Field>
      {outcome === 'success' && (
        <ChecksEditor checks={a.binding.verificationChecks} value={checks} onChange={setChecks} />
      )}
      <Field label="Evidence">
        {(p) => <Textarea {...p} rows={3} value={evidence} onChange={(e) => setEvidence(e.target.value)} />}
      </Field>
      <Field label="Reason">
        {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
      <ErrorAlert error={m.error} />
    </Modal>
  );
}
